import type { Role } from "./roles";

/**
 * Клиент администрирования пользователей (E1, экран «Настройки → Пользователи»). Доступен
 * только администратору — сервер проверяет право `user:manage` на каждом маршруте (матрица
 * 4.3); скрытие раздела в навигации это лишь дублирует. Пути относительные (`/api/...`),
 * сессия — cookie HttpOnly (Р-5), токенов в JS нет.
 *
 * Форма пользователя согласована с сервером по значениям (AdminUserView из
 * apps/api/src/users/store.ts) — клиент и api типы напрямую не делят.
 */

/** Пользователь для админ-экрана — ответ /api/users (без хеша пароля). */
export interface AdminUser {
  readonly id: string;
  readonly login: string;
  readonly fullName: string;
  readonly roles: Role[];
  readonly isActive: boolean;
  /** Задан ли пароль (иначе войти нельзя — нужен сброс пароля администратором). */
  readonly hasPassword: boolean;
  /**
   * D-15: выданный администратором пароль ещё не сменён — до смены пользователю доступны
   * только экран смены пароля и выход. Администратору это видно в списке, чтобы «человек
   * не может работать» читалось на экране, а не выяснялось звонком.
   */
  readonly mustChangePassword: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Данные формы создания пользователя. */
export interface CreateUserInput {
  readonly login: string;
  readonly fullName: string;
  readonly roles: Role[];
  /**
   * Попросить сервер сгенерировать стартовый пароль (E1a). Экран всегда идёт этим путём —
   * администратор пароль не вводит; сервер вернёт его один раз в `generatedPassword`.
   */
  readonly generatePassword?: boolean;
}

/** Результат создания: сам пользователь и (если сервер сгенерировал) одноразовый пароль. */
export interface CreatedUser {
  readonly user: AdminUser;
  /** Стартовый пароль — показывается администратору один раз, нигде не сохраняется (E1a). */
  readonly generatedPassword?: string;
}

/** Изменяемые поля (любое подмножество). */
export interface UpdateUserInput {
  readonly fullName?: string;
  readonly roles?: Role[];
  readonly isActive?: boolean;
}

/** Успех со значением либо типизированная причина отказа с готовым сообщением для UI. */
export type ApiResult<T> =
  | { readonly ok: true; readonly value: T }
  | {
      readonly ok: false;
      readonly reason: "unauthorized" | "forbidden" | "conflict" | "validation" | "network";
      readonly message: string;
    };

const JSON_HEADERS = { "Content-Type": "application/json", Accept: "application/json" } as const;

interface ErrorBody {
  error?: string;
  message?: string;
}

/** Переводит HTTP-ответ ошибки в типизированный отказ; message — с сервера (уже на русском). */
async function toFailure(res: Response): Promise<Extract<ApiResult<never>, { ok: false }>> {
  if (res.status === 401) {
    return { ok: false, reason: "unauthorized", message: "Сессия истекла. Войдите заново." };
  }
  const body = (await res.json().catch(() => null)) as ErrorBody | null;
  const message = body?.message;
  if (res.status === 403) {
    return { ok: false, reason: "forbidden", message: message ?? "Недостаточно прав." };
  }
  if (res.status === 409) {
    return { ok: false, reason: "conflict", message: message ?? "Конфликт данных." };
  }
  if (res.status === 400) {
    return { ok: false, reason: "validation", message: message ?? "Проверьте введённые данные." };
  }
  return { ok: false, reason: "network", message: message ?? "Не удалось выполнить операцию." };
}

/** Список пользователей (GET /api/users). */
export async function fetchUsers(): Promise<ApiResult<AdminUser[]>> {
  let res: Response;
  try {
    res = await fetch("/api/users", {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as { users?: AdminUser[] } | null;
  return { ok: true, value: body?.users ?? [] };
}

/** Создание пользователя (POST /api/users). Возвращает пользователя и одноразовый пароль (E1a). */
export async function createUser(input: CreateUserInput): Promise<ApiResult<CreatedUser>> {
  let res: Response;
  try {
    res = await fetch("/api/users", {
      method: "POST",
      headers: JSON_HEADERS,
      credentials: "same-origin",
      body: JSON.stringify(input),
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as {
    user?: AdminUser;
    generatedPassword?: string;
  } | null;
  if (!body?.user) {
    return { ok: false, reason: "network", message: "Неожиданный ответ сервера." };
  }
  return { ok: true, value: { user: body.user, generatedPassword: body.generatedPassword } };
}

/** Изменение пользователя: роли, ФИО, активность (PATCH /api/users/:id). */
export async function updateUser(
  id: string,
  patch: UpdateUserInput,
): Promise<ApiResult<AdminUser>> {
  let res: Response;
  try {
    res = await fetch(`/api/users/${encodeURIComponent(id)}`, {
      method: "PATCH",
      headers: JSON_HEADERS,
      credentials: "same-origin",
      body: JSON.stringify(patch),
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as { user?: AdminUser } | null;
  if (!body?.user) {
    return { ok: false, reason: "network", message: "Неожиданный ответ сервера." };
  }
  return { ok: true, value: body.user };
}

/**
 * Сброс пароля пользователю с серверной генерацией (E1a, POST /api/users/:id/reset-password).
 * Экран всегда запрашивает генерацию — сервер возвращает новый пароль один раз для показа
 * администратору; прежние сессии пользователя при этом отзываются (Р-5). Свой пароль так не сбросить.
 */
export async function resetPassword(id: string): Promise<ApiResult<{ generatedPassword: string }>> {
  let res: Response;
  try {
    res = await fetch(`/api/users/${encodeURIComponent(id)}/reset-password`, {
      method: "POST",
      headers: JSON_HEADERS,
      credentials: "same-origin",
      body: JSON.stringify({ generatePassword: true }),
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as { generatedPassword?: string } | null;
  if (!body?.generatedPassword) {
    return { ok: false, reason: "network", message: "Неожиданный ответ сервера." };
  }
  return { ok: true, value: { generatedPassword: body.generatedPassword } };
}
